// UI-FILTROS-01 (D4, D6, PLAN §2.3): o filtro avançado em pílulas fala o payload de hoje.
// Uma pílula por atributo, "E" entre pílulas e "OU" dentro de uma pílula (vários valores). O que
// sai daqui segue o caminho de sempre: filterQueryGenerator, POST /filter, custom_filters.query.
//
// Pílula: { attributeKey, filterOperator, values, attributeModel, customAttributeType }
// Payload: [{ attributeKey, filterOperator, values, queryOperator, attributeModel, customAttributeType }]

// Operadores que só existem na pílula. O backend recebe o que cada um vira.
export const PILL_OPERATORS = {
  // "entre": > d1−1 e < d2+1, porque os dois do backend são estritos.
  BETWEEN: 'between',
  // "tem todas" (etiqueta): N itens equal_to de 1 valor, ligados por E.
  HAS_ALL: 'has_all',
};

const SEM_VALOR = ['is_present', 'is_not_present'];

export const operatorHasValue = operator => !SEM_VALOR.includes(operator);

// O tipo da pílula sai do tipo de entrada que o provider dá ao atributo.
export const pillKind = filterType => {
  if (filterType?.attributeKey === 'labels') return 'labels';
  const tipo = filterType?.inputType;
  if (tipo === 'multiSelect' || tipo === 'searchSelect') return 'list';
  if (tipo === 'booleanSelect') return 'boolean';
  if (tipo === 'date') return 'date';
  if (tipo === 'number') return 'number';
  return 'text';
};

// Os operadores de cada atributo são os do provider (G3 = b), mais "entre" onde ele já tem
// "depois" e "antes", e "tem todas" na etiqueta.
export const pillOperators = filterType => {
  const doProvider = (filterType?.filterOperators || []).map(
    operador => operador.value
  );
  const operadores = [...doProvider];
  if (pillKind(filterType) === 'labels') {
    operadores.splice(
      operadores.indexOf('equal_to') + 1,
      0,
      PILL_OPERATORS.HAS_ALL
    );
  }
  if (
    doProvider.includes('is_greater_than') &&
    doProvider.includes('is_less_than')
  ) {
    operadores.splice(
      operadores.indexOf('is_less_than') + 1,
      0,
      PILL_OPERATORS.BETWEEN
    );
  }
  return operadores;
};

const doisDigitos = numero => String(numero).padStart(2, '0');

// A data vai em YYYY-MM-DD no fuso do navegador (o dia que a pessoa escolheu).
export const isoDay = data =>
  `${data.getFullYear()}-${doisDigitos(data.getMonth() + 1)}-${doisDigitos(data.getDate())}`;

const comoDia = valor =>
  valor instanceof Date ? isoDay(valor) : String(valor);

const mudarDia = (valor, dias) => {
  const [ano, mes, dia] = comoDia(valor).split('-').map(Number);
  return isoDay(new Date(ano, mes - 1, dia + dias));
};

// "12" vira 12; o que não é número vai como veio.
const comoNumero = valor => {
  const texto = String(valor).trim();
  const numero = Number(texto);
  return texto !== '' && Number.isFinite(numero) ? numero : valor;
};

const tipoDe = (filterTypes, attributeKey) =>
  filterTypes.find(tipo => tipo.attributeKey === attributeKey);

const ehPersonalizado = (pill, filterType) =>
  (filterType?.attributeModel ?? pill.attributeModel) === 'customAttributes';

// Pílula → itens do payload. Cada item leva "and"; o filterQueryGenerator tira o do último.
const itensDaPilula = (pill, filterType) => {
  const item = (filterOperator, values) => ({
    attributeKey: pill.attributeKey,
    filterOperator,
    values,
    queryOperator: 'and',
    attributeModel: pill.attributeModel ?? filterType?.attributeModel,
    customAttributeType: pill.customAttributeType ?? '',
  });
  const valores = pill.values || [];

  if (pill.filterOperator === PILL_OPERATORS.BETWEEN) {
    const [inicio, fim] = valores;
    return [
      item('is_greater_than', [mudarDia(inicio, -1)]),
      item('is_less_than', [mudarDia(fim, 1)]),
    ];
  }
  if (pill.filterOperator === PILL_OPERATORS.HAS_ALL) {
    return valores.map(valor => item('equal_to', [valor]));
  }
  if (!operatorHasValue(pill.filterOperator)) {
    return [item(pill.filterOperator, [])];
  }
  if (pillKind(filterType) === 'date') {
    const dias = pill.filterOperator === 'days_before';
    return [
      item(
        pill.filterOperator,
        valores.map(valor => (dias ? valor : comoDia(valor)))
      ),
    ];
  }
  if (pillKind(filterType) === 'number') {
    // O campo devolve texto ("12"). O backend aceita os dois, mas o casamento em tempo real
    // (filterHelpers.js, equalTo) compara por igual com o número da conversa: com "12", a
    // conversa que o backend manda some da lista.
    return [item(pill.filterOperator, valores.map(comoNumero))];
  }
  return [item(pill.filterOperator, valores)];
};

// Os personalizados vão por último: o backend não zerava o tipo do personalizado, e a condição
// seguinte perdia valores (P1.12). O D5 corrige lá; aqui a ordem evita o caso de vez.
export const pillsToPayload = (pills, filterTypes = []) => {
  const comTipo = pills.map(pill => ({
    pill,
    tipo: tipoDe(filterTypes, pill.attributeKey),
  }));
  return [
    ...comTipo.filter(({ pill, tipo }) => !ehPersonalizado(pill, tipo)),
    ...comTipo.filter(({ pill, tipo }) => ehPersonalizado(pill, tipo)),
  ].flatMap(({ pill, tipo }) => itensDaPilula(pill, tipo));
};

// O payload guardado traz ids, objetos { id, name } (o modal antigo) ou um valor solto.
const valoresSimples = values => {
  if (values === undefined || values === null || values === '') return [];
  const lista = Array.isArray(values) ? values : [values];
  return lista.map(valor =>
    valor && typeof valor === 'object' && 'id' in valor ? valor.id : valor
  );
};

// O id que veio de um link é texto ("3"); o da opção, número. A pílula compara por igual.
const comIdDaOpcao = (valores, filterType) => {
  const opcoes = filterType?.options || [];
  return valores.map(valor => {
    const opcao = opcoes.find(({ id }) => String(id) === String(valor));
    return opcao ? opcao.id : valor;
  });
};

const pilulaDoItem = (item, filterType) => {
  const kind = pillKind(filterType);
  const valores = valoresSimples(item.values);
  return {
    attributeKey: item.attributeKey,
    filterOperator: item.filterOperator,
    values:
      kind === 'list' || kind === 'labels'
        ? comIdDaOpcao(valores, filterType)
        : valores,
    attributeModel: item.attributeModel,
    customAttributeType: item.customAttributeType ?? '',
  };
};

const operadorDaLigacao = item =>
  String(item.queryOperator || 'and').toLowerCase();

// Os itens de um atributo viram uma pílula, ou null quando não cabem numa.
const juntarDoAtributo = (itens, filterType) => {
  const pilulas = itens.map(item => pilulaDoItem(item, filterType));
  const operadores = pillOperators(filterType);
  if (pilulas.length === 1) {
    return operadores.includes(pilulas[0].filterOperator) ? pilulas[0] : null;
  }

  const [primeira] = pilulas;
  const porOperador = operador =>
    pilulas.filter(pilula => pilula.filterOperator === operador);
  const maior = porOperador('is_greater_than');
  const menor = porOperador('is_less_than');
  if (
    pilulas.length === 2 &&
    maior.length === 1 &&
    menor.length === 1 &&
    operadores.includes(PILL_OPERATORS.BETWEEN)
  ) {
    return {
      ...primeira,
      filterOperator: PILL_OPERATORS.BETWEEN,
      values: [
        mudarDia(maior[0].values[0], 1),
        mudarDia(menor[0].values[0], -1),
      ],
    };
  }

  const todasDeUmValor = pilulas.every(
    pilula => pilula.filterOperator === 'equal_to' && pilula.values.length === 1
  );
  if (todasDeUmValor && operadores.includes(PILL_OPERATORS.HAS_ALL)) {
    return {
      ...primeira,
      filterOperator: PILL_OPERATORS.HAS_ALL,
      values: pilulas.map(pilula => pilula.values[0]),
    };
  }
  return null;
};

const soLeitura = (payload, filterTypes, motivo) => ({
  pills: payload.map(item =>
    pilulaDoItem(item, tipoDe(filterTypes, item.attributeKey))
  ),
  representavel: false,
  motivo,
});

// Payload (visão salva, filtro aplicado, barra básica) → pílulas. `representavel: false` quando
// não cabe em pílulas sem mudar o resultado; as pílulas voltam uma por item, só para leitura.
// motivo: 'or' (OU entre atributos), 'attribute' (atributo que a conta não tem mais),
// 'operator' (operador que a pílula não oferece) ou 'repeated' (atributo repetido).
export const payloadToPills = (payload = [], filterTypes = []) => {
  if (!payload.length) return { pills: [], representavel: true, motivo: null };

  const ligacoes = payload.slice(0, -1).map(operadorDaLigacao);
  if (ligacoes.includes('or')) {
    // "status é aberta OU status é pendente" cabe numa pílula de lista; entre atributos, não.
    const filterType = tipoDe(filterTypes, payload[0].attributeKey);
    const mesmaLista =
      ['list', 'labels'].includes(pillKind(filterType)) &&
      payload.every(
        item =>
          item.attributeKey === payload[0].attributeKey &&
          item.filterOperator === 'equal_to'
      );
    if (!mesmaLista || ligacoes.includes('and')) {
      return soLeitura(payload, filterTypes, 'or');
    }
    const pilula = pilulaDoItem(payload[0], filterType);
    const valores = payload.flatMap(
      item => pilulaDoItem(item, filterType).values
    );
    return {
      pills: [{ ...pilula, values: [...new Set(valores)] }],
      representavel: true,
      motivo: null,
    };
  }

  const chaves = [...new Set(payload.map(item => item.attributeKey))];
  const pills = [];
  for (let i = 0; i < chaves.length; i += 1) {
    const filterType = tipoDe(filterTypes, chaves[i]);
    if (!filterType) return soLeitura(payload, filterTypes, 'attribute');
    const itens = payload.filter(item => item.attributeKey === chaves[i]);
    const pilula = juntarDoAtributo(itens, filterType);
    if (!pilula) {
      return soLeitura(
        payload,
        filterTypes,
        itens.length === 1 ? 'operator' : 'repeated'
      );
    }
    pills.push(pilula);
  }
  return { pills, representavel: true, motivo: null };
};

// O resumo dos filtros aplicados (ActiveFilterPreview) lê as pílulas, e não o payload: o nome da
// opção no lugar do id ("Brasil", não "BR"), o "entre" com os dias escolhidos (o payload leva
// d1−1 e d2+1) e o "tem todas" num item só. Quando o payload não cabe em pílulas, sai um item por
// condição, com a ligação dela.
export const payloadToSummary = (payload = [], filterTypes = []) => {
  const { pills, representavel } = payloadToPills(payload, filterTypes);
  return pills.map((pill, indice) => {
    const opcoes = tipoDe(filterTypes, pill.attributeKey)?.options || [];
    const nome = valor => {
      const opcao = opcoes.find(({ id }) => String(id) === String(valor));
      return opcao ? (opcao.name ?? opcao.title ?? valor) : valor;
    };
    return {
      attributeKey: pill.attributeKey,
      filterOperator: pill.filterOperator,
      queryOperator: representavel ? 'and' : operadorDaLigacao(payload[indice]),
      values: pill.values.map(nome),
    };
  });
};

// A confirmação do D6: a visão com OU vira E. O resultado muda, e por isso só com o "sim".
export const withAndOnly = payload =>
  payload.map(item => ({ ...item, queryOperator: 'and' }));
